import { uploadData, getUrl, remove } from 'aws-amplify/storage'
import map from 'lodash/map'
import axios, { AxiosRequestConfig } from 'axios'
import S3 from 'aws-sdk/clients/s3'
import { FILEPROXY_URL, VOTIRO_URL } from './api-url'
import { getAuthAdapter } from '@shell/adapters/auth'

/**
 * Sign out the current user. Delegates to AuthAdapter.signOut()
 * which handles: API logout call, Redux dispatch, storage clear,
 * Amplify signOut, and SSO redirect.
 */
export async function signOutUser() {
  return getAuthAdapter().signOut()
}

type StorageAccessLevel = 'guest' | 'protected' | 'private'

export async function uploadFile(files: any, level: StorageAccessLevel = 'protected') {
  const uuidv1 = require('uuid/v1')

  const pFiles = map(files, (file: any) => {
    const fileName = file.name.replace(/ /g, '_')
    const key = `${uuidv1()}-${fileName}`

    return uploadData({
      key,
      data: file,
      options: {
        contentType: file.type,
        accessLevel: level,
      },
    })
      .result.then(() => ({
        name: fileName,
        path: key,
      }))
      .catch((err: any) => {
        __DEV__ && console.log('err: %o', err)
      })
  })

  return Promise.all(pFiles)
}

export const formatFileName = (fileName: string) => {
  const currentTime = new Date().getTime()
  const newName = `${currentTime}_${fileName.replace(/[^a-zA-Z0-9-_.]/g, '')}`
  console.log('fileName _ ', newName)
  return newName
}

export async function uploadFileProxy(files: any) {
  const pFiles = map(files, (file: any) => {
    const fileName = file.name.replace(/ /g, '_')
    const uploadedFileName = formatFileName(file.name)

    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${FILEPROXY_URL}/${process.env.AWS_S3_BUCKET}/${uploadedFileName}?content-type=${file.type}`,
      data: file,
    }

    return axios(config)
      .then(() => ({
        name: fileName,
        path: uploadedFileName,
      }))
      .catch((err) => {
        __DEV__ && console.log('err: %o', err)
      })
  })

  return Promise.all(pFiles)
}

export async function votiroFileUpload(files: any) {
  const pFiles = map(files, (file: any) => {
    const fileName = file.name.replace(/ /g, '_')

    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${VOTIRO_URL}/votiroFileupload?filename=${fileName}&contentType=${file.type}`,
      data: file,
      headers: {
        Accept: 'application/json',
      },
    }

    return axios(config)
      .then((response) => {
        return {
          name: fileName,
          id: response.data.id,
          path: response.data.url,
          type: file.type,
          report: response.data.report,
        }
      })
      .catch((err) => {
        __DEV__ && console.log('err: %o', err)
      })
  })

  return Promise.all(pFiles)
}

export async function votiroFileDownload(file: any) {
  window.open(`${VOTIRO_URL}/votiroFileupload/${file.id}?filename=${file.name}&contentType=${file.type}`)
}

export async function votiroFileReport(id: any) {
  const config: AxiosRequestConfig = {
    method: 'get',
    url: `${VOTIRO_URL}/votiroFileupload/${id}/report`,
    headers: {
      Accept: 'application/json',
    },
  }

  return axios(config)
    .then((response) => response.data)
    .catch((err) => {
      __DEV__ && console.log('err: %o', err)
    })
}

export function getFileProxyBaseUrl(url: string) {
  return `${FILEPROXY_URL}/${process.env.AWS_S3_BUCKET}/${url}`
}

export async function getFileBaseUrl(url: string, level: StorageAccessLevel = 'protected') {
  const result = await getUrl({
    key: url,
    options: { accessLevel: level },
  })
  // v6 getUrl() returns { url: URL } — convert to string and strip query params
  return result.url.toString().split('?')[0]
}

export async function removeFile(name: string) {
  return remove({
    key: name,
  })
}

export async function getFile(name: string, level: StorageAccessLevel = 'protected') {
  const result = await getUrl({
    key: name,
    options: { accessLevel: level },
  })
  // v6 getUrl() returns { url: URL } — convert to string for backward compat
  return result.url.toString()
}

export const getS3Details = (url: string) => {
  const splitted = decodeURIComponent(url).split('/')
  const length = splitted?.length ?? 0
  if (length > 2) {
    return {
      key: splitted[length - 1],
      identityId: splitted[length - 2],
    }
  }
  return {
    key: url,
    identityId: undefined,
  }
}

export async function getProtectedFile(url: string, isSystem: boolean) {
  const { key, identityId } = getS3Details(url)
  if (url?.includes('/public/') || url?.includes('/proctected')) {
    const result = await getUrl({
      key,
      options: {
        accessLevel: isSystem ? 'guest' : 'protected',
        ...(identityId ? { targetIdentityId: identityId } : {}),
      },
    })
    // v6 getUrl() returns { url: URL } — convert to string for backward compat
    return result.url.toString()
  } else {
    return await getSignedURL(key)
  }
}

export async function getSignedURL(key: string) {
  const credentials = await getAuthAdapter().getCredentials()

  const s3 = new S3({
    region: process.env.AWS_S3_REGION,
    credentials: credentials,
  })

  const params = {
    Bucket: process.env.AWS_S3_BUCKET,
    Key: key,
  }

  return await s3.getSignedUrlPromise('getObject', params)
}

/**
 * Configure Amplify Auth + Storage.
 * In v6, both are configured via Amplify.configure() in the auth adapter.
 * This function delegates to the adapter which handles the unified config.
 */
export const configureAmplify = (state: string | string[] | null) => {
  getAuthAdapter().configureAuth(state as string | null)
}
